import * as VoiceService from '@/admin/services/VoiceService'
import {
  generateAxialysToken,
  getAllInboundCallConfigurations,
  getAllOutboundCallConfigurations,
  getAllProvidersConfigurations,
  getOdigoConfiguration,
  getOdigoInboundCallConfigurations,
  getOdigoOutboundCallConfigurations,
} from '@/admin/services/VoiceService'
import {
  type IFrameStyle,
  type InboundCallConfigurationDTOV2,
  type OdigoConfigurationDTOV1,
  type OutboundCallConfigurationDTOV2,
  type ProviderGeneralConfigurationDTOV2,
  VoiceProvider,
  type AgentModel,
} from '@/admin/types/voice'
import {
  CtiEventSource,
  CtiInteractionTypeEnum,
  CtiLandingPageEnum,
  type CtiOdigoAgentAndStatus,
  type CtiService,
  CtiStatus,
  CtiStatusColor,
  ctiStatusColorMap,
} from '@/cti/types/cti'
import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import { AuthoritiesEnum } from '@/common/types/session'
import { useSessionStore } from '@/common/stores/sessionStore'
import router from '@/router'
import { APP_ROUTE_NAMES } from '@/app/types/router'
import { useVoiceStore } from '@/admin/stores/voiceStore'
import { useScriptTag } from '@vueuse/core'

const dummyCtiService: CtiService = {
  outboundCallFromEverDesk() {
    /* Dummy == NoOp */
  },
  release() {
    /* Dummy == NoOp */
  },
  setAgentStatus() {
    /* Dummy == NoOp */
  },
}

const blankURL = new URL('about:blank')

export const useCtiStore = defineStore('cti', () => {
  const sessionStore = useSessionStore()

  const activeProvider: Ref<VoiceProvider> = ref(VoiceProvider.NONE)
  const token: Ref<string> = ref('')
  const toReinitialized: Ref<boolean> = ref(false)
  const connectable: Ref<boolean> = ref(false)
  const voiceIsInitialized: Ref<boolean> = ref(false)
  const dialogVisibility: Ref<boolean> = ref(false)
  const dialogStateSource: Ref<CtiEventSource> = ref(CtiEventSource.Automatic)
  const callerPhone: Ref<string | null> = ref('')
  const service: Ref<CtiService> = ref(dummyCtiService)
  const idGroup: Ref<string | null> = ref(null)
  const outboundCallConfiguration: Ref<OutboundCallConfigurationDTOV2 | null> = ref(null)
  const url: Ref<URL> = ref(blankURL)
  const inboundCallsMap: Ref<Map<string, InboundCallConfigurationDTOV2>> = ref(new Map())
  const outboundCallsMap: Ref<Map<string, OutboundCallConfigurationDTOV2>> = ref(new Map())
  const urlString: Ref<string> = ref('')
  const calledPhone: Ref<string | null> = ref(null)
  const customerId: Ref<number | null> = ref(null)
  const idCall: Ref<string | null> = ref(null)
  const direction: Ref<CtiInteractionTypeEnum | null> = ref(null)
  const updateAtEnd: Ref<boolean> = ref(false)
  const landingPage: Ref<CtiLandingPageEnum> = ref(CtiLandingPageEnum.TICKET_HANDLING_SCREEN)
  const status: Ref<CtiStatus> = ref(CtiStatus.DISCONNECTED)
  const agentLogin: Ref<string | null> = ref(null)
  const ticketId: Ref<number | null> = ref(null)
  const userToUserInformation: Ref<string | undefined> = ref()
  const iframeStyle: Ref<IFrameStyle> = ref({
    narrowWidth: '320px',
    wideWidth: '560px',
    height: '200vh',
    innerPadding: '48px',
  })
  const genesysConfiguration: Ref<ProviderGeneralConfigurationDTOV2 | undefined> = ref(undefined)
  const iframeUrlUpdate: Ref<boolean> = ref(false)

  const inhouseConfiguration: Ref<ProviderGeneralConfigurationDTOV2 | undefined> = ref(undefined)
  const axialysConfiguration: Ref<ProviderGeneralConfigurationDTOV2 | undefined> = ref(undefined)
  const five9Configuration: Ref<ProviderGeneralConfigurationDTOV2 | undefined> = ref(undefined)
  const odigoAgentStatus: Ref<string | null> = ref(null)
  const incorrectUser: Ref<boolean> = ref(false)

  const odigoConfiguration: Ref<OdigoConfigurationDTOV1 | undefined> = ref(undefined)

  function statusColor(): CtiStatusColor {
    return ctiStatusColorMap.get(status.value) ?? CtiStatusColor.black
  }

  function toggleDialog(source: CtiEventSource) {
    dialogVisibility.value = !dialogVisibility.value
    dialogStateSource.value = source
  }

  function showDialog(source: CtiEventSource) {
    dialogVisibility.value = true
    dialogStateSource.value = source
  }

  function hideDialog(source: CtiEventSource) {
    dialogVisibility.value = false
    dialogStateSource.value = source
  }

  function newInboundCall(
    newCallerPhone: string,
    newIdGroup: string | null,
    newCalledPhone: string | null,
    newIdCall: string | null,
    newUpdateAtEnd: boolean,
    newUuiData?: string,
  ) {
    direction.value = CtiInteractionTypeEnum.INBOUND
    showDialog(CtiEventSource.Automatic)
    callerPhone.value = newCallerPhone
    idGroup.value = newIdGroup
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    updateAtEnd.value = newUpdateAtEnd
    userToUserInformation.value = newUuiData
  }

  function newOutboundCall(
    newCallerPhone: string | null,
    newIdGroup: string | null,
    newCalledPhone: string | null,
    newCustomerId: number | null,
    newIdTicket: number | null,
    newIdCall: string | null,
    newUpdateAtEnd: boolean,
  ) {
    status.value = CtiStatus.ON_CALL
    direction.value = CtiInteractionTypeEnum.OUTBOUND
    showDialog(CtiEventSource.Automatic)
    callerPhone.value = newCallerPhone
    idGroup.value = newIdGroup
    outboundCallConfiguration.value = newIdGroup ? (outboundCallsMap.value.get(newIdGroup) ?? null) : null
    calledPhone.value = newCalledPhone
    customerId.value = newCustomerId
    ticketId.value = newIdTicket
    idCall.value = newIdCall
    updateAtEnd.value = newUpdateAtEnd
  }

  function newOutboundCallFromEverDesk(
    newOutboundCallConfiguration: UUID,
    newCalledPhone: string,
    newCustomerId: number | null,
    newIdTicket: number | null,
  ) {
    direction.value = CtiInteractionTypeEnum.OUTBOUND
    showDialog(CtiEventSource.Automatic)
    outboundCallConfiguration.value = [...outboundCallsMap.value.values()].find((aConf) => aConf.id === newOutboundCallConfiguration) ?? null
    calledPhone.value = newCalledPhone
    customerId.value = newCustomerId
    ticketId.value = newIdTicket
    updateAtEnd.value = true
  }

  function endCall(newIdGroup: string | null, newCalledPhone: string | null, newIdCall: string | null) {
    idGroup.value = newIdGroup
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    status.value = CtiStatus.AVAILABLE
  }

  function release() {
    toReinitialized.value = true

    service.value.release()
    service.value = dummyCtiService
  }

  function setAgentStatus(state: string, breakCode?: string) {
    service.value.setAgentStatus(state, breakCode)
  }

  async function initCtiConfig() {
    activeProvider.value = await VoiceService.getVoiceProvider()
    if (activeProvider.value === VoiceProvider.NONE) {
      activeProvider.value = await VoiceService.getDefaultVoiceProvider()
    }
    // default ne devrait pas etre dedans
    console.log('cti - store activeProvider', activeProvider.value, sessionStore.getAuthoritySet.has(AuthoritiesEnum.CTI_ACCESS))
    if (!sessionStore.getAuthoritySet.has(AuthoritiesEnum.CTI_ACCESS)) {
      connectable.value = false
      voiceIsInitialized.value = true
      return
    }

    switch (activeProvider.value) {
      case VoiceProvider.AXIALYS:
        await initWithAxialys()
        break
      case VoiceProvider.ODIGO:
        await initWithOdigo()
        break
      case VoiceProvider.GENESYS:
        await initWithGenesys()
        break
      case VoiceProvider.CTI_INHOUSE:
        await initWithInhouse()
        break
      case VoiceProvider.FIVE_9:
        await initWithFive9()
        break
      case VoiceProvider.NONE:
        connectable.value = false
        console.debug('There is no active voice provider')
        break
      default:
        connectable.value = false
        console.error('The selected voice provider is not implemented')
        break
    }
    voiceIsInitialized.value = true
  }

  function reloadUrl() {
    url.value.searchParams.set('refresh-time', `${new Date().getTime()}`)
    urlString.value = url.value.toString()
  }

  async function initWithAxialys() {
    try {
      const voiceStore = useVoiceStore()
      const configurations = await getAllProvidersConfigurations()
      axialysConfiguration.value = configurations.find(
        (configuration: ProviderGeneralConfigurationDTOV2) => configuration.provider === VoiceProvider.AXIALYS,
      )
      if (!axialysConfiguration.value) {
        throw new Error('Configuration not loaded')
      }
      token.value = (<{ token: string }>await generateAxialysToken()).token
      landingPage.value = <CtiLandingPageEnum>axialysConfiguration?.value.landingPage?.toString()
      connectable.value = true

      if (!axialysConfiguration.value.iframeUrl) {
        throw new Error('Axialys iframe URL not configured')
      }
      url.value = new URL(axialysConfiguration.value.iframeUrl)
      urlString.value = url.value.toString()
      await voiceStore.getAxialysInboundCallConfig()
      voiceStore.axialysInboundCallConfigs.forEach((element) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        element.active && inboundCallsMap.value.set(element.code, element)
      })
      await initInboundsAndOutBounds()
    } catch (e) {
      console.error(e)
      connectable.value = false
    }
  }

  async function initInboundsAndOutBounds() {
    const inboundCalls = await getAllInboundCallConfigurations()
    inboundCallsMap.value.clear()
    inboundCalls.forEach((element) => {
      if (!element.active) return
      inboundCallsMap.value.set(element.code, element)
    })
    outboundCallsMap.value.clear()
    const outboundCalls = await getAllOutboundCallConfigurations()
    outboundCalls.forEach((element) => {
      if (!element.active) return
      outboundCallsMap.value.set(element.code, element)
    })
  }

  async function initWithOdigo() {
    try {
      iframeStyle.value.narrowWidth = '315px'
      iframeStyle.value.wideWidth = '615px'
      iframeStyle.value.height = '78vh'

      odigoConfiguration.value = await getOdigoConfiguration()
      if (!odigoConfiguration.value) {
        throw new Error('Configuration not loaded')
      }
      connectable.value = true
      landingPage.value = <CtiLandingPageEnum>odigoConfiguration.value.commonConfiguration?.landingPage?.toString()

      url.value = new URL(odigoConfiguration.value.commonConfiguration.iframeUrl)
      urlString.value = url.value.toString()
      const inboundCalls = await getOdigoInboundCallConfigurations()
      inboundCallsMap.value.clear()
      inboundCalls.forEach((element) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        element.active && inboundCallsMap.value.set(element.code, element)
      })
      outboundCallsMap.value.clear()
      const outboundCalls = await getOdigoOutboundCallConfigurations()
      outboundCalls.forEach((element) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        element.active && outboundCallsMap.value.set(element.code, element)
      })
    } catch (e) {
      console.error(e)
      connectable.value = false
    }
  }

  async function initWithInhouse() {
    try {
      iframeStyle.value.height = '78vh'

      const configurations = await getAllProvidersConfigurations()
      inhouseConfiguration.value = configurations.find(
        (configuration: ProviderGeneralConfigurationDTOV2) => configuration.provider === VoiceProvider.CTI_INHOUSE,
      )

      if (!inhouseConfiguration.value) {
        throw new Error('Configuration not loaded')
      }

      // Check for iframeUrl at both locations
      const iframeUrl = inhouseConfiguration.value.iframeUrl
      if (!iframeUrl) {
        throw new Error('Inhouse iframe URL not configured')
      }

      // Set up iframe configuration
      url.value = new URL(iframeUrl)
      urlString.value = url.value.toString()

      connectable.value = true

      await initInboundsAndOutBounds()
    } catch (error) {
      console.error('Failed to initialize CTI_INHOUSE:', error)
      connectable.value = false
      throw error
    }
  }

  async function loadFive9SDK(five9Configuration: ProviderGeneralConfigurationDTOV2) {
    const sdkUrl = five9Configuration.providerConfiguration?.five9?.sdkUrl
    if (!sdkUrl) {
      throw new Error('Five9 SDK URL not configured')
    }
    const { load } = useScriptTag(
      sdkUrl,
      (el: HTMLScriptElement) => {
        console.debug('Five9 SDK loaded', el.src)
      },
      { manual: true },
    )
    try {
      await load(true)
    } catch (error) {
      console.error('Failed to load Five9 SDK:', error)
      throw new Error('Failed to initialize CTI_FIVE_9')
    }
  }

  async function initWithFive9() {
    try {
      iframeStyle.value.height = '78vh'

      const configurations = await getAllProvidersConfigurations()
      five9Configuration.value = configurations.find(
        (configuration: ProviderGeneralConfigurationDTOV2) => configuration.provider === VoiceProvider.FIVE_9,
      )

      if (!five9Configuration.value) {
        throw new Error('Configuration not loaded')
      }

      const iframeUrl = five9Configuration.value.iframeUrl
      if (!iframeUrl) {
        throw new Error('Five9 iframe URL not configured')
      }

      await loadFive9SDK(five9Configuration.value)

      url.value = new URL(iframeUrl)
      url.value.searchParams.append('f9crmapi', 'true')
      url.value.searchParams.append('f9verticalthreshold', '300px')
      urlString.value = url.value.toString()

      connectable.value = true

      await initInboundsAndOutBounds()
    } catch (error) {
      console.error('Failed to initialize CTI_FIVE_9:', error)
      connectable.value = false
      throw error
    }
  }

  async function getOdigoAgentAndStatus() {
    try {
      const odigoAgentAndStatus: CtiOdigoAgentAndStatus | null = await VoiceService.getOdigoAgentAndStatus()

      odigoAgentStatus.value = odigoAgentAndStatus?.status || null
      agentLogin.value = odigoAgentAndStatus?.ctiIdentifier || null
    } catch (message) {
      console.error(message)
    }
  }

  function isOutboundcallAllowed(): boolean {
    if (activeProvider.value === VoiceProvider.ODIGO) {
      return [CtiStatus.AVAILABLE, CtiStatus.ON_BREAK].includes(status.value)
    } else {
      return true
    }
  }

  async function initWithGenesys() {
    try {
      iframeStyle.value.height = '78vh'

      const configurations = await getAllProvidersConfigurations()
      genesysConfiguration.value = configurations.find(
        (configuration: ProviderGeneralConfigurationDTOV2) => configuration.provider === VoiceProvider.GENESYS,
      )

      if (!genesysConfiguration.value) {
        throw new Error('Configuration not loaded')
      }
      connectable.value = true

      initGenesysAuthentication()

      if (!genesysConfiguration.value.id) return
      await initInboundsAndOutBounds()
    } catch (e) {
      console.error(e)
      connectable.value = false
    }
  }

  function initGenesysAuthentication() {
    const genesysAuthRoute = router.resolve({
      name: APP_ROUTE_NAMES.GENESYS_AUTH_IFRAME_NAME,
    })
    const authPageAssetUrlString = window.__toCdnUrl(genesysAuthRoute.path.replace('/', ''))
    if (!authPageAssetUrlString) {
      throw new Error(`Invalid URL of Genesys Auth Iframe: ${authPageAssetUrlString}`)
    }
    const authPageAssetUrl = new URL(authPageAssetUrlString)
    authPageAssetUrl.hash = `origin=${window.location.origin}`
    url.value = authPageAssetUrl
    urlString.value = url.value.toString()
  }

  async function getAgentLogin() {
    try {
      const agentData: AgentModel | null = await VoiceService.getAgentLogin()
      agentLogin.value = agentData?.providerLogin ?? null
    } catch (message) {
      console.error(message)
    }
  }

  return {
    activeProvider,
    token,
    toReinitialized,
    connectable,
    voiceIsInitialized,
    dialogVisibility,
    dialogStateSource,
    callerPhone,
    service,
    idGroup,
    outboundCallConfiguration,
    calledPhone,
    customerId,
    url,
    inboundCallsMap,
    outboundCallsMap,
    urlString,
    idCall,
    direction,
    updateAtEnd,
    landingPage,
    status,
    agentLogin,
    ticketId,
    statusColor,
    toggleDialog,
    showDialog,
    hideDialog,
    newInboundCall,
    newOutboundCall,
    newOutboundCallFromEverDesk,
    endCall,
    release,
    initCtiConfig,
    reloadUrl,
    iframeStyle,
    odigoConfiguration,
    odigoAgentStatus,
    getOdigoAgentAndStatus,
    incorrectUser,
    isOutboundcallAllowed,
    genesysConfiguration,
    iframeUrlUpdate,
    initGenesysAuthentication,
    inhouseConfiguration,
    initWithInhouse,
    userToUserInformation,
    setAgentStatus,
    getAgentLogin,
    axialysConfiguration,
    five9Configuration,
    initWithFive9,
  }
})
